/**
 * Live handle derivation for the Beats page (D-14, D-15).
 *
 * A literal port of the DB rules on public.import_beats, so the handle shown
 * while Dominique types is exactly the one the DB will accept, and a refused
 * URL gets a named reason instead of a raw 23514. The DB CHECKs stay the
 * second wall; nothing here widens or narrows them.
 *
 * Mirrored constraints:
 * - import_beats_page_url_facebook (mobile/supabase/migrations/20240101000027_import_beats.sql):
 *   `page_url ~ '^https://(<8 exact hosts>)/[^?#]+$'` → PAGE_URL_FACEBOOK
 * - import_beats_handle_matches_url (0027):
 *   handle = lower(regexp_replace(page_url, <last-segment regex>, '\1')) → LAST_SEGMENT
 *   (the regex is written out next to LAST_SEGMENT; it cannot sit in a block comment)
 * - import_beats_handle_shape (0027):
 *   `handle ~ '^[a-z0-9][a-z0-9._-]*$' AND strpos(handle, '..') = 0` → HANDLE_SHAPE
 *   (also importer.ts HANDLE_PATTERN + isSafeHandle)
 * - import_beats_page_url_single_segment
 *   (mobile/supabase/migrations/20240101000028_newsroom_intake_and_beat_area.sql SECTION 4):
 *   `page_url ~ '^https://[^/]+/[^/?#]+/?$' AND handle NOT IN (<19 names>)`
 *   → SINGLE_SEGMENT + RESERVED
 *
 * Percent-encoding is never decoded: the DB does not decode either, so an
 * encoded segment keeps its '%' and is refused as bad_handle on both sides.
 * A query string or fragment (Facebook share links carry ?mibextid=, ?ref=, …)
 * is dropped before the rules run, so the stored page_url never has one; the
 * DB CHECK still refuses one if it ever arrives another way. Owner ruling
 * 2026-10-07, replacing D-14's "refuse, never strip". profile.php?id= is
 * checked first, so a numeric profile is still refused, not stripped to
 * profile.php.
 */

/** The exact host CHECK of import_beats_page_url_facebook (0027). Case-sensitive, like the DB. */
export const PAGE_URL_FACEBOOK =
  /^https:\/\/(facebook\.com|www\.facebook\.com|m\.facebook\.com|mbasic\.facebook\.com|touch\.facebook\.com|web\.facebook\.com|fb\.com|www\.fb\.com)\/[^?#]+$/

/** import_beats_page_url_single_segment (0028): one path segment, optional trailing slash. */
const SINGLE_SEGMENT = /^https:\/\/[^/]+\/[^/?#]+\/?$/

// The DB's regexp_replace in import_beats_handle_matches_url (0027):
//   regexp_replace(page_url, '^.*/([^/]+)/?$', '\1') — no match → string unchanged.
const LAST_SEGMENT = /^.*\/([^/]+)\/?$/

/** import_beats_handle_shape (0027) / importer.ts HANDLE_PATTERN. */
const HANDLE_SHAPE = /^[a-z0-9][a-z0-9._-]*$/

/** The reserved first segments of import_beats_page_url_single_segment (0028 SECTION 4). */
const RESERVED: ReadonlySet<string> = new Set([
  'profile.php',
  'people',
  'pages',
  'groups',
  'events',
  'watch',
  'marketplace',
  'gaming',
  'reel',
  'reels',
  'home.php',
  'permalink.php',
  'story.php',
  'photo.php',
  'photo',
  'share',
  'login',
  'help',
  'search'
])

export type BeatUrlRefusal =
  | 'empty'
  | 'numeric_profile'
  | 'not_https'
  | 'not_facebook'
  | 'not_single_segment'
  | 'reserved'
  | 'bad_handle'

export type DerivedBeat =
  | { ok: true; pageUrl: string; handle: string }
  | { ok: false; reason: BeatUrlRefusal }

/**
 * Derive the stored page_url and handle from a pasted URL, or name why not.
 *
 * Only what cannot change meaning is canonicalised: surrounding whitespace is
 * trimmed, the scheme+host prefix is lowercased (the DB host regex is
 * case-sensitive) and anything from the first ? or # is dropped. The path
 * keeps its case; the vendor receives it verbatim.
 */
export const deriveBeat = (input: string): DerivedBeat => {
  let url = input.trim()
  if (url === '') return { ok: false, reason: 'empty' }

  url = url.replace(/^([a-z][a-z0-9+.-]*:\/\/[^/]+)/i, (prefix) => prefix.toLowerCase())

  // D-15: a numeric profile has no stable handle to file results under.
  if (/\/profile\.php/i.test(url) && /[?&]id=/i.test(url)) {
    return { ok: false, reason: 'numeric_profile' }
  }
  url = url.replace(/[?#].*$/, '')
  if (!url.startsWith('https://')) return { ok: false, reason: 'not_https' }
  if (!PAGE_URL_FACEBOOK.test(url)) return { ok: false, reason: 'not_facebook' }
  if (!SINGLE_SEGMENT.test(url)) return { ok: false, reason: 'not_single_segment' }

  const match = LAST_SEGMENT.exec(url)
  const handle = (match ? match[1] : url).toLowerCase()

  if (RESERVED.has(handle)) return { ok: false, reason: 'reserved' }
  if (!HANDLE_SHAPE.test(handle) || handle.includes('..')) {
    return { ok: false, reason: 'bad_handle' }
  }

  return { ok: true, pageUrl: url, handle }
}
